import * as THREE from 'three';

import AuthClientStore from '../../../common/services/auth-client.service';

/**
 * The phone as the 3D studio's camera — the parts both ends share.
 *
 * Both ends normalise the product model the same way (longest side 2 units,
 * standing on y = 0, centred), so a pose sent from the phone means exactly the
 * same shot in the desktop studio: no calibration, no conversion.
 */

const API_URL = process.env.REACT_APP_PMS_API_URL || 'http://localhost:8082';

/**
 * Where the phone page reaches the API. On a phone the page must be HTTPS
 * (iOS gives motion and camera access only to secure pages); an HTTPS page
 * cannot call an HTTP API, so in that case it goes through its own origin —
 * the dev server proxies /services and /socket.io to the API (setupProxy.js).
 */
export function vcamApiBase(): string {
    if (window.location.protocol === 'https:' && API_URL.startsWith('http:')) return window.location.origin;
    return API_URL;
}

/** The origin the QR code points the phone at — a tunnel's in development. */
export function vcamPhoneOrigin(): string {
    return process.env.REACT_APP_VCAM_PUBLIC_ORIGIN || window.location.origin;
}

export interface VcamSessionInfo { code: string; token: string; productName?: string }

export async function openVcamSession(productUuid?: string): Promise<VcamSessionInfo> {
    const res = await fetch(`${API_URL}/services/pms/content-studio/vcam/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${AuthClientStore.getAccessToken()}` },
        body: JSON.stringify({ productUuid }),
    });
    if (!res.ok) throw new Error(`Could not open a camera session (${res.status})`);
    return res.json();
}

export const vcamPhoneUrl = (s: VcamSessionInfo) => `${vcamPhoneOrigin()}/vcam/${s.code}?t=${encodeURIComponent(s.token)}`;

/** What travels from the phone, many times a second. */
export interface VcamPose { pos: [number, number, number]; quat: [number, number, number, number]; lensMm: number; t: number }
/** Button presses from the phone. */
export type VcamControl = { action: 'record' } | { action: 'stop' } | { action: 'keyframe' } | { action: 'scene' };
/** What the studio tells the phone. */
export interface VcamState {
    recording: boolean; scenes: number; scene: number; keyframes: number; seconds?: number;
    /** The product's placement in the studio, so the phone shows it the same way. */
    model?: { pos: [number, number, number]; rot: [number, number, number]; scale: [number, number, number] };
    /** Tiles instead of a model: the slabs as the studio lays them, in metres. */
    surface?: VcamSurfaceState;
}

/** One slab of a tile layout: where its centre is and how it is turned. */
export interface VcamPiece { pos: [number, number, number]; rot: [number, number, number]; scale?: [number, number, number] }
/** The tile studio's layout — every slab is tileW × thickness × tileH metres, textured on top. */
export interface VcamSurfaceState { tileW: number; tileH: number; thickness: number; pieces: VcamPiece[] }

/**
 * The slabs as the phone should draw them, from the studio's live objects.
 *
 * Indexed by piece number, and a gap (a piece unmounting mid-relayout) is
 * skipped rather than sent as a hole the phone would have to guess at.
 */
export function surfacePieces(objects: Map<number, THREE.Object3D>): VcamPiece[] {
    return Array.from(objects.entries())
        .sort(([a], [b]) => a - b)
        .map(([, o]) => o)
        .filter(Boolean)
        .map((o) => ({
            pos: [o.position.x, o.position.y, o.position.z],
            rot: [o.rotation.x, o.rotation.y, o.rotation.z],
            scale: [o.scale.x, o.scale.y, o.scale.z],
        }));
}

/** What the phone is filming, as the session reports it. */
export interface VcamInfo {
    productName: string | null;
    kind: 'model' | 'surface';
    hasModel: boolean;
    sizeM: number | null;
    /** Tiles only: one piece's real size, the fallback until the studio sends its layout. */
    tile: { wM: number; hM: number; thicknessMm: number } | null;
}

/**
 * Read GET /vcam/:code. An API from before tiles had no `kind`, and a session
 * with a product was always a model — so that is what a missing kind means.
 */
export function parseVcamInfo(j: any): VcamInfo {
    const kind = j?.kind === 'surface' ? 'surface' : 'model';
    const num = (v: any, d: number) => (typeof v === 'number' && v > 0 ? v : d);
    return {
        productName: j?.productName ?? null,
        kind,
        hasModel: kind === 'model' && !!j?.hasModel,
        sizeM: typeof j?.sizeM === 'number' ? j.sizeM : null,
        tile: kind === 'surface'
            ? { wM: num(j?.tile?.wM, 0.6), hM: num(j?.tile?.hM, 0.6), thicknessMm: num(j?.tile?.thicknessMm, 10) }
            : null,
    };
}

/** Longest side 2 units, base on y = 0, centred — the studio's own normalisation. Returns the centre height. */
export function normaliseModel(object: THREE.Object3D): number {
    const box = new THREE.Box3().setFromObject(object);
    const size = box.getSize(new THREE.Vector3());
    object.scale.setScalar(2 / (Math.max(size.x, size.y, size.z) || 1));
    object.updateMatrixWorld(true);
    const box2 = new THREE.Box3().setFromObject(object);
    const centre = box2.getCenter(new THREE.Vector3());
    object.position.x -= centre.x;
    object.position.z -= centre.z;
    object.position.y -= box2.min.y;
    return (box2.max.y - box2.min.y) / 2 || 0.9;
}

/** Where a pose looks: a point ahead of the camera, at the subject's distance. */
export function poseTarget(pose: VcamPose, subjectHeight = 0.9): [number, number, number] {
    const pos = new THREE.Vector3(...pose.pos);
    const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(new THREE.Quaternion(...pose.quat));
    const dist = Math.max(0.5, pos.distanceTo(new THREE.Vector3(0, subjectHeight, 0)));
    const t = pos.add(fwd.multiplyScalar(dist));
    return [t.x, t.y, t.z];
}
